// FILE: /src/Components/Dashboard/OnboardingChecklist.js (FULLY UPDATED)

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchOnboardingStatus, markChecklistItemComplete } from '../../store/Slices/DashboardSlice';
import { Progress, Button, Spin, message } from 'antd';
import Confetti from 'react-confetti';

// --- Icon Mapping for each task key ---
const taskIcons = {
    created_brand: { icon: 'fa-solid fa-tags', color: 'text-blue-500' },
    connected_profile: { icon: 'fa-solid fa-share-nodes', color: 'text-pink-500' },
    completed_brand_kit: { icon: 'fa-solid fa-palette', color: 'text-purple-500' },
    scheduled_post: { icon: 'fa-solid fa-calendar-days', color: 'text-green-500' },
    created_draft: { icon: 'fa-solid fa-file-lines', color: 'text-yellow-500' },
    uploaded_to_library: { icon: 'fa-solid fa-images', color: 'text-indigo-500' },
    used_ai_caption: { icon: 'fa-solid fa-wand-magic-sparkles', color: 'text-cyan-500' },
    used_ai_image: { icon: 'fa-solid fa-image', color: 'text-teal-500' },
    used_social_post_generator: { icon: 'fa-solid fa-robot', color: 'text-orange-500' },
    added_competitor: { icon: 'fa-solid fa-binoculars', color: 'text-red-500' },
};

// --- Sub-component for the "Next Step" card ---
const FocusTaskCard = ({ item, onCtaClick }) => {
    const { title, description, ctaText, ctaAction, ctaLink, key } = item;
    const navigate = useNavigate();
    const { icon, color } = taskIcons[key] || { icon: 'fa-solid fa-star', color: 'text-gray-500' };

    const handleClick = () => {
        if (ctaAction === 'navigate') {
            navigate(ctaLink);
        } else if (ctaAction === 'complete_and_navigate') {
            onCtaClick(key);
            navigate(ctaLink);
        }
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-brand-primary/50 shadow-lg mt-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className={`flex-shrink-0 w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-md`}>
                    <i className={`${icon} ${color} text-3xl`}></i>
                </div>
                <div className="flex-grow">
                    <p className="font-semibold text-brand-primary text-sm">NEXT STEP</p>
                    <h4 className="font-bold text-gray-900 text-lg">{title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{description}</p>
                </div>
                <Button type="primary" size="large" onClick={handleClick} className="mt-4 sm:mt-0 flex-shrink-0">
                    {ctaText}
                </Button>
            </div>
        </div>
    );
};

// --- Sub-component for the rest of the list ---
const ChecklistItem = ({ item }) => {
    const { title, isComplete, key } = item;
    const { icon } = taskIcons[key] || { icon: 'fa-solid fa-circle-notch' };

    return (
        <div className={`flex items-center gap-4 transition-opacity ${isComplete ? 'opacity-50' : 'opacity-100'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-green-100' : 'bg-gray-200'}`}>
                <i className={`fa-solid ${isComplete ? 'fa-check text-green-600' : icon + ' text-gray-500'}`}></i>
            </div>
            <h5 className={`font-medium ${isComplete ? 'text-gray-500 line-through' : 'text-gray-700'}`}>{title}</h5>
        </div>
    );
};

// --- Main Component ---
export default function OnboardingChecklist() {
    const dispatch = useDispatch();
    const { checklist, status } = useSelector(state => state.dashboard);
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        dispatch(fetchOnboardingStatus());
    }, [dispatch]);

    const isGroupComplete = (group) => group && group.every(item => item.isComplete);

    const activeGroup = useMemo(() => {
        if (!checklist) return null;
        if (!isGroupComplete(checklist.group1)) return { title: "Let's Get Your Account Ready", items: checklist.group1 };
        if (!isGroupComplete(checklist.group2)) return { title: "Master the Basics", items: checklist.group2 };
        if (!isGroupComplete(checklist.group3)) return { title: "Explore AI Features", items: checklist.group3 };
        return null; // All groups are complete
    }, [checklist]);
    
    const nextIncompleteTask = useMemo(() => {
        return activeGroup?.items.find(item => !item.isComplete);
    }, [activeGroup]);
    
    const { totalCount, completedCount } = useMemo(() => {
        if (!checklist) return { totalCount: 0, completedCount: 0 };
        const allItems = [...(checklist.group1 || []), ...(checklist.group2 || []), ...(checklist.group3 || [])];
        const completed = allItems.filter(item => item.isComplete).length;
        return { totalCount: allItems.length, completedCount: completed };
    }, [checklist]);
    
    useEffect(() => {
        if (totalCount > 0 && completedCount === totalCount) {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 5000); // Confetti for 5 seconds
        }
    }, [completedCount, totalCount]);

    const handleCtaClick = (taskKey) => {
        dispatch(markChecklistItemComplete(taskKey)).unwrap().catch(err => message.error(err));
    };

    if (status === 'loading') {
        // --- THIS IS THE FIX ---
        // Removed `tip` prop and added a <p> tag for the text.
        return (
            <div className="bg-white p-6 rounded-xl shadow-lg border mb-8 text-center">
                <Spin size="large" />
                <p className="mt-2 text-gray-500">Loading Your Progress...</p>
            </div>
        );
        // --- END OF FIX ---
    }

    if (!activeGroup) {
        return null; // Hide when all tasks are complete
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border mb-8 animate-fade-in">
            {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} />}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">Your Onboarding Guide</h3>
                    <p className="text-sm text-gray-500">Complete these steps to unlock Oolook's full potential.</p>
                </div>
                <div className="text-sm font-medium text-gray-500 mt-2 sm:mt-0">{completedCount} / {totalCount} completed</div>
            </div>
            <Progress percent={Math.round((completedCount / totalCount) * 100)} showInfo={false} strokeColor={{ from: '#805ad5', to: '#6D28D9' }} trailColor="#e2e8f0" size="small" />
            
            {nextIncompleteTask && <FocusTaskCard item={nextIncompleteTask} onCtaClick={handleCtaClick} />}

            <div className="mt-8">
                <h4 className="font-semibold text-gray-600 mb-4">{activeGroup.title}</h4>
                <div className="space-y-4">
                    {activeGroup.items.map((item) => <ChecklistItem key={item.key} item={item} />)}
                </div>
            </div>
        </div>
    );
}